import React, {useCallback, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';

import {useGate, useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import * as yup from 'yup';

import {LanguageSelector} from '@/widgets/LanguageSelector';
import {ROUTES} from '@app/routing';
import {PasswordGate, passwordModel} from '@entities/password/model';
import {history} from '@shared/lib/history';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Input} from '@shared/ui/Input';

import {getPasswordSchema} from './helpers/passwordSchema';
import {PasswordValidationRules} from './helpers/passwordValidationRules';
import {
    ButtonContainer,
    Field,
    Fields,
    Form,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    MessageWrapper,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
} from './styled';

type ResetPasswordForm = {password: string; passwordConfirm: string};

export function ResetPasswordForm() {
    const {t, i18n} = useTranslation();
    const {search} = useLocation();
    useGate(PasswordGate);

    const token = useMemo(() => new URLSearchParams(search).get('token'), [search]);
    const password = useUnit(passwordModel);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) => t(`password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                password: getPasswordSchema(translateValidates),
                passwordConfirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .oneOf([yup.ref('password')], translateValidates('confirm_password_mismatch')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, watch, errors} = useForm<ResetPasswordForm>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            password: '',
            passwordConfirm: '',
        },
    });

    const watchPassword = watch('password', '');

    const onLogin = () => history.push(ROUTES.LOGIN);

    const onSubmit = async (data: ResetPasswordForm) => {
        if (!token) return;
        try {
            await password.set({
                tokenType: 'RESET_PASSWORD',
                password: data.password,
                confirmPassword: data.passwordConfirm,
                token,
            });
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <div>
                <ServiceTitle size={4}>traceCORE B2B E-Invoicing</ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    {!password.state.success && !password.state.expired && (
                        <>
                            <PasswordTitle size={3}>{t('password.reset.title')}</PasswordTitle>
                            <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                                <FormDescription>{t('password.reset.description')}</FormDescription>
                                <Fields>
                                    <Field isError={!!errors.password}>
                                        <span>{t('password.reset.fields.newPassword.label')}</span>
                                        <Controller
                                            name="password"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    type="password"
                                                    placeholder={t(
                                                        'password.reset.fields.newPassword.placeholder',
                                                    )}
                                                    autoComplete="new-password"
                                                    disabled={password.state.pending}
                                                    hasError={!!errors.password}
                                                    isValid={
                                                        formState.dirtyFields.password &&
                                                        !errors.password
                                                    }
                                                />
                                            )}
                                        />
                                        <PasswordValidationRules watchValue={watchPassword} />
                                    </Field>

                                    <Field
                                        isError={!!errors.passwordConfirm}
                                        text={errors.passwordConfirm?.message}
                                    >
                                        <span>
                                            {t('password.reset.fields.passwordConfirmation.label')}
                                        </span>
                                        <Controller
                                            name="passwordConfirm"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    type="password"
                                                    placeholder={t(
                                                        'password.reset.fields.passwordConfirmation.placeholder',
                                                    )}
                                                    autoComplete="new-password"
                                                    disabled={password.state.pending}
                                                    hasError={!!errors.passwordConfirm}
                                                    isValid={
                                                        formState.dirtyFields.passwordConfirm &&
                                                        !errors.passwordConfirm
                                                    }
                                                />
                                            )}
                                        />
                                    </Field>
                                </Fields>

                                <ButtonContainer>
                                    <Button
                                        type="submit"
                                        isLoading={password.state.pending}
                                        disabled={!formState.isValid}
                                    >
                                        {t('password.reset.submitButton')}
                                    </Button>
                                    <Button
                                        variant={ButtonVariant.Text}
                                        onClick={onLogin}
                                        isLoading={password.state.pending}
                                    >
                                        {t('password.reset.returnButton')}
                                    </Button>
                                </ButtonContainer>
                            </Form>
                        </>
                    )}
                    {password.state.success && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('password.reset.success.title')}
                            </PasswordTitle>
                            <Button onClick={onLogin}>{t('password.reset.success.submit')}</Button>
                        </MessageWrapper>
                    )}
                    {password.state.expired && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('password.set.expired.title')}
                            </PasswordTitle>
                        </MessageWrapper>
                    )}
                </PasswordContent>
            </MainContainer>
        </>
    );
}
